import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';

import { BasicFilterSelect, BasicSelect } from 'ui/selects';
import { SelectWrapper } from 'ui/selects/Selects.styles';

import { getFilterSegmentSelectInputTriggerText } from 'modules/journey/components/FilterInputsList/utils';
import { FilterSegmentSelectInputOption } from '../FilterInputsList.types';
import { FilterSegmentButton } from './FilterSegmentButton';
import { FilterSegmentErrorButton } from './FilterSegmentErrorButton';

export type FilterSegmentSelectInputProps<OptionType> = {
  options: OptionType[];
  selectedOptionIds: string[];
  onSelect: (selectedOptions: OptionType[]) => void;
  id: string;
  placeholder: string;
  isDisabled?: boolean;
  isFilterable?: boolean;
  onSearchChange?: (filterText: string | undefined) => void;
  hasError?: boolean;
};

type TriggerProps = {
  placeholder: string;
  selectedOptions: FilterSegmentSelectInputOption[];
  toggleDropdown: () => void;
  isOpen: boolean;
  isError: boolean;
  isDisabled: boolean;
};

const StyledSelectWrapper = styled(SelectWrapper)`
  border: none;
`;

const Trigger = forwardRef<HTMLDivElement, TriggerProps>(
  ({ placeholder, selectedOptions, toggleDropdown, isOpen, isError, isDisabled }, ref) => {
    const text = useMemo(
      () => getFilterSegmentSelectInputTriggerText(selectedOptions, placeholder),
      [selectedOptions, placeholder],
    );

    const ButtonComponent = isError ? FilterSegmentErrorButton : FilterSegmentButton;
    return (
      <div ref={ref}>
        <ButtonComponent onClick={toggleDropdown} isToggled={isOpen} isDisabled={isDisabled}>
          {text}
        </ButtonComponent>
      </div>
    );
  },
);

export const FilterSegmentSelectInput = <OptionType extends FilterSegmentSelectInputOption>({
  options,
  onSelect,
  selectedOptionIds,
  id,
  placeholder,
  isDisabled,
  isFilterable = false,
  hasError = false,
  onSearchChange,
}: FilterSegmentSelectInputProps<OptionType>) => {
  const Select = isFilterable ? BasicFilterSelect : BasicSelect;
  return (
    <Select
      id={id}
      isError={hasError}
      defaultValues={selectedOptionIds}
      onSelect={(selectedIds) => {
        onSelect(options.filter((option) => selectedIds.includes(option.id)));
      }}
      options={options}
      /** @ts-ignore */
      Trigger={Trigger}
      SelectWrapper={StyledSelectWrapper}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isVirtualized={true}
      onSearchChange={onSearchChange}
      disableInternalFiltering={typeof onSearchChange === 'function'}
    />
  );
};
